/**
 * @param {number} amount
 * @param {number} decimalPlaces
 * @param {string} symbol
 * @returns {string}
 */
export function formatCurrencyValue(amount, decimalPlaces, symbol) {
	const sign = amount < 0 ? '-' : '';
	const formatted = Math.abs(amount).toLocaleString('en-US', {
		minimumFractionDigits: decimalPlaces,
		maximumFractionDigits: decimalPlaces
	});
	return `${sign}${symbol}${formatted}`;
}

/**
 * Format a currency table cell, returning the em-dash "unavailable" marker
 * when the value or its currency metadata is missing. Renders nothing that
 * could misrepresent the amount (e.g. a currency code masquerading as a
 * symbol or a wrong number of decimal places).
 * @param {number | null | undefined} value
 * @param {number | undefined} decimalPlaces
 * @param {string | undefined} symbol
 * @returns {string}
 */
export function formatCurrencyCell(value, decimalPlaces, symbol) {
	if (value === null || value === undefined || !Number.isFinite(value)) { return '\u2014'; }
	if (!symbol || typeof decimalPlaces !== 'number' || !Number.isFinite(decimalPlaces)) { return '\u2014'; }
	return formatCurrencyValue(value, decimalPlaces, symbol);
}

/**
 * Format a signed difference (e.g. +$50.00 or -$50.00) for use in balance projections.
 * @param {number} diff
 * @param {number} decimalPlaces
 * @param {string} symbol
 * @returns {string}
 */
export function formatSignedDiff(diff, decimalPlaces, symbol) {
	if (diff > 0) { return `+${formatCurrencyValue(diff, decimalPlaces, symbol)}`; }
	return formatCurrencyValue(diff, decimalPlaces, symbol);
}

/**
 * Format a full balance projection line: Name: $BEFORE → $AFTER (+/-$DIFF)
 * @param {string} text
 * @param {number} before
 * @param {number} after
 * @param {{ decimalPlaces: number, symbol: string }} currency
 * @returns {string}
 */
export function formatProjection(text, before, after, currency) {
	const diff = after - before;
	return `${text}: ${formatCurrencyValue(before, currency.decimalPlaces, currency.symbol)} \u2192 ${formatCurrencyValue(after, currency.decimalPlaces, currency.symbol)} (${formatSignedDiff(diff, currency.decimalPlaces, currency.symbol)})`;
}
